import { useEffect, useMemo, useRef, useState } from 'react';
import * as XLSX from 'xlsx-js-style';
import { AdvancedTable } from '../components/AdvancedTable';
import { Plus, Edit, Trash2, X, Save, CheckCircle, XCircle, Loader2, Search, Upload, ChevronLeft, ChevronRight, Users, UserCheck, Calendar as CalendarIcon, Activity, FileText, Eye } from 'lucide-react';
import { driverService } from '../services/driverService';
import { documentService } from '../services/documentService';
import { authService } from '../services/authService';
import { useAppAlert } from '../components/AppAlertProvider';

interface LeaveRequest {
  id: string;
  driverId: string;
  driverName: string;
  leaveType: string;
  startDate: string;
  endDate: string;
  days: number;
  reason: string;
  requestDate: string;
  status: 'Beklemede' | 'Onaylandı' | 'Reddedildi';
  approver?: string;
  notes?: string;
  documents?: LeaveRequestDocument[];
}

interface LeaveRequestDocument {
  id: string;
  fileName: string;
  filePath: string;
  mimeType: string;
  uploadedAt: string;
}

interface DriverOption {
  id: string;
  name: string;
  tcNumber?: string;
}

interface LeaveBalanceSummary {
  driverId: string;
  driverUserId: string | null;
  tcNumber: string;
  driverName: string;
  phone: string;
  remainingDays: number;
  addedDays: number;
  deductedDays: number;
  usedDays: number;
}

interface LeaveBalanceTransaction {
  id: string;
  driverId: string;
  transactionType: string;
  daysDelta: number;
  description: string;
  createdByName?: string | null;
  createdAt: string;
}

interface CalendarLeaveDriverDetail {
  driverId: string;
  driverName: string;
  phone: string;
  vehiclePlate: string;
  trailerPlate: string;
  leaveType: string;
  startDate: string;
  endDate: string;
  reason: string;
}

interface CalendarDayData {
  date: string;
  activeCount: number;
  totalDrivers: number;
  onLeaveCount: number;
  onLeaveDrivers: CalendarLeaveDriverDetail[];
}

type LeaveRequestStatus = LeaveRequest['status'];

const defaultFormData: Partial<LeaveRequest> = {
  status: 'Beklemede',
  requestDate: new Date().toISOString().split('T')[0],
  days: 1,
};

const normalizeLeaveStatus = (status?: string): LeaveRequestStatus => {
  switch (status) {
    case 'approved':
    case 'Onaylandı':
      return 'Onaylandı';
    case 'rejected':
    case 'Reddedildi':
      return 'Reddedildi';
    default:
      return 'Beklemede';
  }
};

const getTransactionTypeLabel = (value?: string) => {
  switch (value) {
    case 'manual-add':
      return 'Eklendi';
    case 'manual-subtract':
      return 'Çıkarıldı';
    case 'import-add':
      return 'Excel Eklendi';
    case 'import-subtract':
      return 'Excel Çıkarıldı';
    case 'leave-used':
      return 'Kullandı';
    default:
      return value || '-';
  }
};

const mapLeaveRequest = (item: any): LeaveRequest => ({
  id: String(item.id),
  driverId: String(item.driver_id ?? item.driverId ?? ''),
  driverName: item.driver_name ?? item.driverName ?? '',
  leaveType: item.leave_type ?? item.leaveType ?? '',
  startDate: (item.start_date ?? item.startDate ?? '').split('T')[0],
  endDate: (item.end_date ?? item.endDate ?? '').split('T')[0],
  days: Number(item.days ?? 0),
  reason: item.reason ?? '',
  requestDate: (item.request_date ?? item.requestDate ?? '').split('T')[0],
  status: normalizeLeaveStatus(item.status),
  approver: item.approver_name ?? item.approver ?? '',
  notes: item.notes ?? item.rejection_reason ?? '',
  documents: item.documents ?? [],
});

const mapLeaveDocument = (item: any): LeaveRequestDocument => ({
  id: String(item.id),
  fileName: item.file_name ?? item.fileName ?? 'Belge',
  filePath: item.file_path ?? item.filePath ?? '',
  mimeType: item.mime_type ?? item.mimeType ?? '',
  uploadedAt: item.uploaded_at ?? item.uploadedAt ?? '',
});

const resolveFileUrl = (filePath?: string | null) => {
  if (!filePath) return '#';
  const pathStr = String(filePath);
  if (pathStr.startsWith('http://') || pathStr.startsWith('https://') || pathStr.startsWith('data:')) return pathStr;
  const apiBase = (import.meta.env.VITE_API_URL || 'http://localhost:5000/api').replace(/\/api\/?$/, '');
  return `${apiBase}${pathStr.startsWith('/') ? pathStr : `/${pathStr}`}`;
};

export function DriverLeaveRequests() {
  const { showAlert, showConfirm } = useAppAlert();
  const [activeTab, setActiveTab] = useState<'requests' | 'balances' | 'transactions' | 'calendar'>('requests');
  const [data, setData] = useState<LeaveRequest[]>([]);
  const [drivers, setDrivers] = useState<DriverOption[]>([]);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<LeaveRequest | null>(null);
  const [formData, setFormData] = useState<Partial<LeaveRequest>>(defaultFormData);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [driverSearch, setDriverSearch] = useState('');
  const [showDriverList, setShowDriverList] = useState(false);
  const [leaveBalances, setLeaveBalances] = useState<LeaveBalanceSummary[]>([]);
  const [selectedBalanceDriverId, setSelectedBalanceDriverId] = useState<string>('');
  const [balanceTransactions, setBalanceTransactions] = useState<LeaveBalanceTransaction[]>([]);
  const [balanceFormOpen, setBalanceFormOpen] = useState(false);
  const [balanceSaving, setBalanceSaving] = useState(false);
  const [balanceForm, setBalanceForm] = useState({ driverId: '', transactionType: 'manual-add', days: '', description: '' });
  const [importingExcel, setImportingExcel] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [allTransactions, setAllTransactions] = useState<any[]>([]);
  const [allTxnsLoading, setAllTxnsLoading] = useState(false);
  const [txnStartDate, setTxnStartDate] = useState('');
  const [txnEndDate, setTxnEndDate] = useState('');
  const [calendarYear, setCalendarYear] = useState(new Date().getFullYear());
  const [calendarMonth, setCalendarMonth] = useState(new Date().getMonth() + 1);
  const [calendarData, setCalendarData] = useState<CalendarDayData[]>([]);
  const [calendarLoading, setCalendarLoading] = useState(false);
  const [selectedCalendarDay, setSelectedCalendarDay] = useState<CalendarDayData | null>(null);
  const [leaveDocumentFile, setLeaveDocumentFile] = useState<File | null>(null);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (activeTab === 'transactions') void loadAllTransactions();
    if (activeTab === 'calendar') void loadCalendar();
  }, [activeTab]);

  useEffect(() => {
    void loadCalendar();
  }, [calendarYear, calendarMonth]);

  const loadAllTransactions = async () => {
    try {
      setAllTxnsLoading(true);
      const response: any = await driverService.getAllLeaveBalanceTransactions(txnStartDate || undefined, txnEndDate || undefined);
      setAllTransactions(Array.isArray(response?.data) ? response.data : []);
    } catch (err: any) {
      console.error('Error loading all transactions:', err);
    } finally {
      setAllTxnsLoading(false);
    }
  };

  const loadCalendar = async () => {
    try {
      setCalendarLoading(true);
      const response: any = await driverService.getActiveDriverCalendar(calendarYear, calendarMonth);
      setCalendarData(Array.isArray(response?.data?.calendar) ? response.data.calendar : []);
    } catch (err: any) {
      console.error('Error loading calendar:', err);
    } finally {
      setCalendarLoading(false);
    }
  };

  const loadData = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const [leaveRes, driversRes, balancesRes] = await Promise.all([
        driverService.getLeaveRequests(),
        driverService.getDrivers({ type: 'internal' }),
        driverService.getLeaveBalances(),
      ]);

      if (leaveRes.success && leaveRes.data) {
        const mappedLeaveRequests = leaveRes.data.map(mapLeaveRequest);
        setData(mappedLeaveRequests);

        const leaveIds = mappedLeaveRequests.map((item) => item.id).filter(Boolean);
        if (leaveIds.length > 0) {
          try {
            const docsResponse: any = await documentService.getDocuments({ entityType: 'driver_leave_request', entityIds: leaveIds.join(',') });
            const docsByLeaveId = new Map<string, LeaveRequestDocument[]>();
            (Array.isArray(docsResponse?.data) ? docsResponse.data : []).forEach((doc: any) => {
              const entityId = String(doc.entity_id ?? doc.entityId ?? '');
              if (!entityId) return;
              const currentDocs = docsByLeaveId.get(entityId) || [];
              currentDocs.push(mapLeaveDocument(doc));
              docsByLeaveId.set(entityId, currentDocs);
            });
            setData(mappedLeaveRequests.map((item) => ({ ...item, documents: docsByLeaveId.get(item.id) || [] })));
          } catch (docError) {
            console.error('Error loading leave request documents:', docError);
          }
        }
      } else {
        setData([]);
      }

      if (driversRes.success && driversRes.data) {
        setDrivers(driversRes.data.map((driver: any) => ({
          id: String(driver.id),
          name: driver.full_name || driver.name || '',
          tcNumber: driver.tc_number || '',
        })));
      } else {
        setDrivers([]);
      }

      setLeaveBalances(Array.isArray(balancesRes?.data) ? balancesRes.data : []);
    } catch (err: any) {
      console.error('Error loading leave requests:', err);
      setError(err.message || 'Veriler yüklenirken hata oluştu');
    } finally {
      setIsLoading(false);
    }
  };

  const columns = [
    { key: 'driverName', label: 'Sürücü Adı', sortable: true, filterable: true },
    { key: 'leaveType', label: 'İzin Türü', sortable: true, filterable: true, groupable: true },
    { key: 'startDate', label: 'Başlangıç', sortable: true, filterable: true },
    { key: 'endDate', label: 'Bitiş', sortable: true, filterable: true },
    { key: 'days', label: 'Gün Sayısı', sortable: true, filterable: true },
    { key: 'reason', label: 'Sebep', sortable: false, filterable: true },
    { key: 'requestDate', label: 'Talep Tarihi', sortable: true, filterable: true },
    { key: 'status', label: 'Durum', sortable: true, filterable: true, groupable: true },
    { key: 'approver', label: 'Onaylayan', sortable: false, filterable: true },
    {
      key: 'documents', label: 'Belge', sortable: false, filterable: false,
      render: (_: any, item: LeaveRequest) => {
        const latestDocument = item.documents?.[0];
        if (!latestDocument) return <span className="text-gray-400">-</span>;
        return (
          <button type="button" onClick={() => window.open(resolveFileUrl(latestDocument.filePath), '_blank', 'noopener,noreferrer')} className="inline-flex items-center gap-1 text-blue-700 hover:text-blue-900">
            <FileText className="h-4 w-4" />
            Görüntüle
          </button>
        );
      }
    },
    {
      key: 'id', label: 'İşlemler', sortable: false, filterable: false,
      render: (_: any, item: LeaveRequest) => (
        <div className="flex gap-2">
          {item.status === 'Beklemede' && (
            <>
              <button onClick={() => updateStatus(item, 'Onaylandı')} className="p-1 text-green-600 hover:bg-green-50 rounded" title="Onayla"><CheckCircle className="w-4 h-4" /></button>
              <button onClick={() => updateStatus(item, 'Reddedildi')} className="p-1 text-red-600 hover:bg-red-50 rounded" title="Reddet"><XCircle className="w-4 h-4" /></button>
            </>
          )}
          <button onClick={() => openForm(item)} className="p-1 text-blue-600 hover:bg-blue-50 rounded"><Edit className="w-4 h-4" /></button>
          <button onClick={() => handleDelete(item.id)} className="p-1 text-red-600 hover:bg-red-50 rounded"><Trash2 className="w-4 h-4" /></button>
        </div>
      )
    },
  ];

  const filteredDriverOptions = useMemo(() => {
    const q = driverSearch.trim().toLocaleLowerCase('tr-TR');
    if (!q) return drivers;
    return drivers.filter((driver) => driver.name.toLocaleLowerCase('tr-TR').includes(q));
  }, [drivers, driverSearch]);

  useEffect(() => {
    const targetDriverId = selectedBalanceDriverId || leaveBalances[0]?.driverId || '';
    if (!targetDriverId) {
      setBalanceTransactions([]);
      return;
    }

    const loadTransactions = async () => {
      try {
        const response: any = await driverService.getDriverLeaveBalance(targetDriverId);
        setBalanceTransactions(Array.isArray(response?.data?.transactions) ? response.data.transactions : []);
      } catch (err: any) {
        console.error('Error loading leave balance transactions:', err);
      }
    };

    void loadTransactions();
  }, [selectedBalanceDriverId, leaveBalances]);

  const openForm = (item?: LeaveRequest) => {
    if (item) {
      setEditingItem(item);
      setFormData(item);
      setDriverSearch(item.driverName || '');
    } else {
      setEditingItem(null);
      setFormData(defaultFormData);
      setDriverSearch('');
    }
    setLeaveDocumentFile(null);
    setIsFormOpen(true);
  };

  const closeForm = () => {
    setIsFormOpen(false);
    setEditingItem(null);
    setFormData(defaultFormData);
    setLeaveDocumentFile(null);
    setDriverSearch('');
    setShowDriverList(false);
  };

  const openBalanceForm = (driverId?: string) => {
    setBalanceForm({ driverId: driverId || selectedBalanceDriverId || leaveBalances[0]?.driverId || '', transactionType: 'manual-add', days: '', description: '' });
    setBalanceFormOpen(true);
  };

  const closeBalanceForm = () => {
    setBalanceFormOpen(false);
    setBalanceForm({ driverId: '', transactionType: 'manual-add', days: '', description: '' });
  };

  const calculateDays = (start: string, end: string): number => {
    if (!start || !end) return 0;
    const startDate = new Date(start);
    const endDate = new Date(end);
    const diffTime = Math.abs(endDate.getTime() - startDate.getTime());
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
  };

  const buildPayload = () => {
    const days = calculateDays(formData.startDate || '', formData.endDate || '');
    return {
      driverId: formData.driverId || '',
      driverName: formData.driverName || '',
      leaveType: formData.leaveType || '',
      startDate: formData.startDate || '',
      endDate: formData.endDate || '',
      requestDate: formData.requestDate || new Date().toISOString().split('T')[0],
      reason: formData.reason || '',
      days,
      notes: formData.notes || '',
    };
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);

    try {
      const payload = buildPayload();
      const response = editingItem
        ? await driverService.updateLeaveRequest(editingItem.id, payload)
        : await driverService.createLeaveRequest(payload);

      if (!response.success) {
        throw new Error(response.message || 'İzin talebi kaydedilemedi');
      }

      const leaveRequestId = editingItem?.id || response.data?.id;
      if (leaveDocumentFile && leaveRequestId) {
        const uploadPayload = new FormData();
        uploadPayload.append('file', leaveDocumentFile);
        uploadPayload.append('entityType', 'driver_leave_request');
        uploadPayload.append('entityId', String(leaveRequestId));
        uploadPayload.append('documentType', 'leave-request-document');
        uploadPayload.append('name', leaveDocumentFile.name);
        await documentService.uploadDocument(uploadPayload);
      }

      await loadData();
      closeForm();
    } catch (err: any) {
      console.error('Error saving leave request:', err);
      showAlert({ tone: 'error', title: 'Kayıt Başarısız', message: err.message || 'Kayıt sırasında hata oluştu' });
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!(await showConfirm({ title: 'İzin Talebi Sil', message: 'Bu izin talebini silmek istediğinizden emin misiniz?', confirmText: 'Sil', tone: 'warning' }))) return;

    try {
      const response = await driverService.deleteLeaveRequest(id);
      if (!response.success) {
        throw new Error(response.message || 'İzin talebi silinemedi');
      }
      await loadData();
    } catch (err: any) {
      console.error('Error deleting leave request:', err);
      showAlert({ tone: 'error', title: 'Silme Başarısız', message: err.message || 'Silme sırasında hata oluştu' });
    }
  };

  const updateStatus = async (item: LeaveRequest, status: 'Onaylandı' | 'Reddedildi') => {
    const currentUser = authService.getStoredUser();

    try {
      const response = status === 'Onaylandı'
        ? await driverService.approveLeaveRequest(item.id, currentUser?.id || '', item.notes || '')
        : await driverService.rejectLeaveRequest(item.id, currentUser?.id || '', item.notes || 'Talep reddedildi');

      if (!response.success) {
        throw new Error(response.message || 'İzin talebi durumu güncellenemedi');
      }

      await loadData();
    } catch (err: any) {
      console.error('Error updating leave request status:', err);
      showAlert({ tone: 'error', title: 'Durum Güncellenemedi', message: err.message || 'Durum güncellenirken hata oluştu' });
    }
  };

  const submitBalanceTransaction = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setBalanceSaving(true);
      const response: any = await driverService.createLeaveBalanceTransaction({
        driverId: balanceForm.driverId,
        transactionType: balanceForm.transactionType,
        days: Number(balanceForm.days || 0),
        description: balanceForm.description,
      });
      if (!response?.success) throw new Error(response?.message || 'İzin hareketi kaydedilemedi');
      await loadData();
      setSelectedBalanceDriverId(balanceForm.driverId);
      closeBalanceForm();
    } catch (err: any) {
      console.error('Balance transaction save failed:', err);
      showAlert({ tone: 'error', title: 'Kayıt Başarısız', message: err.message || 'İzin hareketi kaydedilemedi' });
    } finally {
      setBalanceSaving(false);
    }
  };

  const handleExcelImport = async (file: File | null) => {
    if (!file) return;
    try {
      setImportingExcel(true);
      const buffer = await file.arrayBuffer();
      const workbook = XLSX.read(buffer, { type: 'array' });
      const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
      if (!firstSheet) throw new Error('Excel sayfası bulunamadı');
      const rows: any[] = XLSX.utils.sheet_to_json(firstSheet, { defval: '' });
      const mapped = rows.map((row) => ({
        tcNumber: row['TC'] || row['TC No'] || row['TC Kimlik No'] || row['tcNumber'] || row['tc_number'] || '',
        days: row['Gün'] || row['Gun'] || row['İzin Hakkı'] || row['Izin Hakki'] || row['days'] || row['balance'] || '',
        description: row['Açıklama'] || row['Aciklama'] || row['description'] || 'Excel toplu izin hakkı yüklemesi',
      })).filter((row) => row.tcNumber && row.days !== '');

      if (mapped.length === 0) throw new Error('Excel içinde geçerli TC ve gün satırı bulunamadı');

      const response: any = await driverService.importLeaveBalances(mapped);
      if (!response?.success) throw new Error(response?.message || 'Excel içe aktarma başarısız');
      await loadData();
      showAlert({ tone: 'success', title: 'İçe Aktarım Tamamlandı', message: response.message || 'İzin hareketleri içe aktarıldı' });
    } catch (err: any) {
      console.error('Leave balance excel import failed:', err);
      showAlert({ tone: 'error', title: 'Excel İçe Aktarma Hatası', message: err.message || 'Excel içe aktarma başarısız' });
    } finally {
      setImportingExcel(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const renderCell = (item: LeaveRequest, key: string) => {
    if (key === 'status') {
      const colors = {
        Beklemede: 'bg-yellow-100 text-yellow-800',
        Onaylandı: 'bg-green-100 text-green-800',
        Reddedildi: 'bg-red-100 text-red-800',
      };
      return <span className={`px-2 py-1 rounded-full text-xs font-medium ${colors[item.status]}`}>{item.status}</span>;
    }

    if (key === 'startDate' || key === 'endDate' || key === 'requestDate') {
      return item[key as keyof LeaveRequest] ? new Date(String(item[key as keyof LeaveRequest])).toLocaleDateString('tr-TR') : '-';
    }

    return item[key as keyof LeaveRequest] || '-';
  };

  const renderActions = (item: LeaveRequest) => (
    <div className="flex gap-2">
      {item.status === 'Beklemede' && (
        <>
          <button onClick={() => updateStatus(item, 'Onaylandı')} className="p-1 text-green-600 hover:bg-green-50 rounded" title="Onayla">
            <CheckCircle className="w-4 h-4" />
          </button>
          <button onClick={() => updateStatus(item, 'Reddedildi')} className="p-1 text-red-600 hover:bg-red-50 rounded" title="Reddet">
            <XCircle className="w-4 h-4" />
          </button>
        </>
      )}
      <button onClick={() => openForm(item)} className="p-1 text-blue-600 hover:bg-blue-50 rounded">
        <Edit className="w-4 h-4" />
      </button>
      <button onClick={() => handleDelete(item.id)} className="p-1 text-red-600 hover:bg-red-50 rounded">
        <Trash2 className="w-4 h-4" />
      </button>
    </div>
  );

  const tabs = [
    { key: 'requests', label: 'İzin Talepleri', icon: CalendarIcon },
    { key: 'balances', label: 'Sürücü İzin Kalanı', icon: UserCheck },
    { key: 'transactions', label: 'İzin Hareketliliği', icon: Activity },
    { key: 'calendar', label: 'Sürücü Aktif Kaynak Takvimi', icon: CalendarIcon },
  ] as const;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Sürücü İzin Yönetimi</h1>
          <p className="text-gray-500 mt-1">İzin talepleri, hakedişler, hareketler ve kaynak takvimi</p>
        </div>
      </div>

      <div className="flex items-center gap-1 border-b border-gray-200 pb-0.5 overflow-x-auto">
        {tabs.map((tab) => {
          const Icon = tab.icon;
          return (
            <button key={tab.key} type="button" onClick={() => setActiveTab(tab.key)} className={`inline-flex items-center gap-2 whitespace-nowrap border-b-2 px-4 py-3 text-sm font-medium transition-colors ${activeTab === tab.key ? 'border-blue-600 text-blue-700' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
              <Icon className="h-4 w-4" />
              {tab.label}
            </button>
          );
        })}
      </div>

      {activeTab === 'requests' && (
        <>
          <div className="flex items-center justify-end gap-2">
            <button onClick={() => openForm()} className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700">
              <Plus className="w-5 h-5" />
              Yeni İzin Talebi
            </button>
          </div>

          {isLoading && (
            <div className="bg-white rounded-lg border border-gray-200 py-16 flex justify-center">
              <div className="flex flex-col items-center gap-3">
                <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
                <p className="text-gray-500">İzin talepleri yükleniyor...</p>
              </div>
            </div>
          )}

          {error && !isLoading && (
            <div className="bg-white rounded-lg border border-red-200 py-12 text-center">
              <p className="text-red-600 mb-4">{error}</p>
              <button onClick={loadData} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
                Tekrar Dene
              </button>
            </div>
          )}

          {!isLoading && !error && (
            <AdvancedTable
              data={data as any}
              columns={columns as any}
              onRefresh={loadData}
              onRowDoubleClick={(record: any) => openForm(record as LeaveRequest)}
              disablePagination
            />
          )}
        </>
      )}

      {activeTab === 'balances' && (
        <div className="space-y-6">
          <div className="flex items-center justify-end gap-2">
            <button type="button" onClick={() => fileInputRef.current?.click()} disabled={importingExcel} className="flex items-center gap-2 bg-blue-50 text-blue-700 px-4 py-2 rounded-lg border border-blue-200 hover:bg-blue-100 disabled:opacity-60">
              {importingExcel ? <Loader2 className="w-5 h-5 animate-spin" /> : <Upload className="w-5 h-5" />}
              Excel'den Yükle
            </button>
            <input ref={fileInputRef} type="file" accept=".xlsx,.xls,.csv" className="hidden" onChange={(e) => void handleExcelImport(e.target.files?.[0] || null)} />
            <button onClick={() => openBalanceForm()} className="flex items-center gap-2 bg-emerald-600 text-white px-4 py-2 rounded-lg hover:bg-emerald-700">
              <Plus className="w-5 h-5" />
              İzin Hakkı İşle
            </button>
          </div>

          <div className="grid grid-cols-1 gap-6 xl:grid-cols-[minmax(0,1.25fr)_minmax(320px,0.75fr)]">
            <div className="rounded-xl border border-gray-200 bg-white overflow-hidden">
              <div className="border-b border-gray-200 px-5 py-4">
                <h2 className="text-lg font-semibold text-gray-900">İzin Hakedişleri</h2>
                <p className="mt-1 text-sm text-gray-500">Kalan izin, kullanılan izin ve manuel hakediş hareketleri</p>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left font-medium text-gray-500">Sürücü</th>
                      <th className="px-4 py-3 text-left font-medium text-gray-500">TC</th>
                      <th className="px-4 py-3 text-right font-medium text-gray-500">Eklenen</th>
                      <th className="px-4 py-3 text-right font-medium text-gray-500">Kullanılan</th>
                      <th className="px-4 py-3 text-right font-medium text-gray-500">Kalan</th>
                      <th className="px-4 py-3 text-right font-medium text-gray-500">İşlem</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {leaveBalances.map((item) => (
                      <tr key={item.driverId} className={`cursor-pointer hover:bg-gray-50 ${selectedBalanceDriverId === item.driverId ? 'bg-blue-50' : ''}`} onClick={() => setSelectedBalanceDriverId(item.driverId)}>
                        <td className="px-4 py-3">
                          <div className="font-medium text-gray-900">{item.driverName}</div>
                          <div className="text-xs text-gray-500">{item.phone || '-'}</div>
                        </td>
                        <td className="px-4 py-3 font-mono text-gray-700">{item.tcNumber || '-'}</td>
                        <td className="px-4 py-3 text-right text-emerald-700 font-medium">{item.addedDays.toLocaleString('tr-TR')}</td>
                        <td className="px-4 py-3 text-right text-amber-700 font-medium">{item.usedDays.toLocaleString('tr-TR')}</td>
                        <td className="px-4 py-3 text-right font-semibold text-gray-900">{item.remainingDays.toLocaleString('tr-TR')}</td>
                        <td className="px-4 py-3 text-right">
                          <button type="button" onClick={(e) => { e.stopPropagation(); openBalanceForm(item.driverId); }} className="rounded-lg bg-slate-100 px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-200">Ekle/Çıkar</button>
                        </td>
                      </tr>
                    ))}
                    {leaveBalances.length === 0 && (
                      <tr><td colSpan={6} className="px-4 py-10 text-center text-gray-500">İzin hakedişi kaydı bulunmuyor.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="rounded-xl border border-gray-200 bg-white overflow-hidden">
              <div className="border-b border-gray-200 px-5 py-4">
                <h2 className="text-lg font-semibold text-gray-900">İzin Hareketleri</h2>
                <p className="mt-1 text-sm text-gray-500">Seçili sürücünün transaction geçmişi</p>
              </div>
              <div className="max-h-[420px] overflow-auto">
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left font-medium text-gray-500">Tip</th>
                      <th className="px-4 py-3 text-right font-medium text-gray-500">Gün</th>
                      <th className="px-4 py-3 text-left font-medium text-gray-500">Açıklama</th>
                      <th className="px-4 py-3 text-left font-medium text-gray-500">Tarih</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {balanceTransactions.map((item) => (
                      <tr key={item.id}>
                        <td className="px-4 py-3 text-gray-700">{getTransactionTypeLabel(item.transactionType)}</td>
                        <td className={`px-4 py-3 text-right font-semibold ${item.daysDelta >= 0 ? 'text-emerald-700' : 'text-rose-700'}`}>{item.daysDelta.toLocaleString('tr-TR')}</td>
                        <td className="px-4 py-3 text-gray-700">{item.description || '-'}</td>
                        <td className="px-4 py-3 text-gray-500 whitespace-nowrap">{item.createdAt ? new Date(item.createdAt).toLocaleString('tr-TR') : '-'}</td>
                      </tr>
                    ))}
                    {balanceTransactions.length === 0 && (
                      <tr><td colSpan={4} className="px-4 py-10 text-center text-gray-500">Hareket bulunmuyor.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'transactions' && (
        <div className="rounded-xl border border-gray-200 bg-white overflow-hidden">
          <div className="border-b border-gray-200 px-5 py-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Tüm İzin Hareketleri</h2>
              <p className="mt-1 text-sm text-gray-500">Sisteme kaydedilmiş tüm izin hakediş hareketleri</p>
            </div>
            <div className="flex items-center gap-2">
              <input type="date" value={txnStartDate} onChange={(e) => setTxnStartDate(e.target.value)} className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm" />
              <span className="text-gray-400">-</span>
              <input type="date" value={txnEndDate} onChange={(e) => setTxnEndDate(e.target.value)} className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm" />
              <button type="button" onClick={() => void loadAllTransactions()} className="rounded-lg bg-blue-600 px-3 py-1.5 text-sm text-white hover:bg-blue-700">Filtrele</button>
            </div>
          </div>
          {allTxnsLoading ? (
            <div className="py-10 text-center text-gray-500"><Loader2 className="mx-auto mb-2 h-6 w-6 animate-spin text-blue-600" />Yükleniyor...</div>
          ) : (
            <div className="overflow-x-auto max-h-[70vh] overflow-y-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 sticky top-0">
                  <tr>
                    <th className="px-4 py-3 text-left font-medium text-gray-500">Sürücü</th>
                    <th className="px-4 py-3 text-left font-medium text-gray-500">Tip</th>
                    <th className="px-4 py-3 text-right font-medium text-gray-500">Gün</th>
                    <th className="px-4 py-3 text-left font-medium text-gray-500">Açıklama</th>
                    <th className="px-4 py-3 text-left font-medium text-gray-500">Yetkili</th>
                    <th className="px-4 py-3 text-left font-medium text-gray-500">Tarih</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {allTransactions.map((item: any) => (
                    <tr key={item.id}>
                      <td className="px-4 py-3 font-medium text-gray-900">{item.driverName || '-'}</td>
                      <td className="px-4 py-3 text-gray-700">{getTransactionTypeLabel(item.transactionType)}</td>
                      <td className={`px-4 py-3 text-right font-semibold ${Number(item.daysDelta) >= 0 ? 'text-emerald-700' : 'text-rose-700'}`}>{Number(item.daysDelta).toLocaleString('tr-TR')}</td>
                      <td className="px-4 py-3 text-gray-700 max-w-[300px] truncate">{item.description || '-'}</td>
                      <td className="px-4 py-3 text-gray-700">{item.createdByName || '-'}</td>
                      <td className="px-4 py-3 text-gray-500 whitespace-nowrap">{item.createdAt ? new Date(item.createdAt).toLocaleString('tr-TR') : '-'}</td>
                    </tr>
                  ))}
                  {allTransactions.length === 0 && (
                    <tr><td colSpan={6} className="px-4 py-10 text-center text-gray-500">Hareket bulunmuyor.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {activeTab === 'calendar' && (
        <div className="rounded-xl border border-gray-200 bg-white overflow-hidden">
          <div className="border-b border-gray-200 px-5 py-4 flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Sürücü Aktif Kaynak Takvimi</h2>
              <p className="mt-1 text-sm text-gray-500">Aylık bazda aktif sürücü sayısı ve izinli personel durumu</p>
            </div>
          </div>
          <div className="p-4">
            <div className="flex items-center justify-between mb-4">
              <button type="button" onClick={() => { if (calendarMonth <= 1) { setCalendarYear((y) => y - 1); setCalendarMonth(12); } else { setCalendarMonth((m) => m - 1); } }} className="rounded-lg border border-gray-300 p-2 hover:bg-gray-50">
                <ChevronLeft className="h-5 w-5 text-gray-600" />
              </button>
              <div className="text-lg font-semibold text-gray-900">
                {new Date(calendarYear, calendarMonth - 1).toLocaleString('tr-TR', { month: 'long', year: 'numeric' })}
              </div>
              <button type="button" onClick={() => { if (calendarMonth >= 12) { setCalendarYear((y) => y + 1); setCalendarMonth(1); } else { setCalendarMonth((m) => m + 1); } }} className="rounded-lg border border-gray-300 p-2 hover:bg-gray-50">
                <ChevronRight className="h-5 w-5 text-gray-600" />
              </button>
            </div>

            {calendarLoading ? (
              <div className="py-16 text-center text-gray-500"><Loader2 className="mx-auto mb-2 h-8 w-8 animate-spin text-blue-600" />Takvim yükleniyor...</div>
            ) : (
              <>
                <div className="grid grid-cols-7 gap-1 mb-1">
                  {['Pzt', 'Sal', 'Çar', 'Per', 'Cum', 'Cmt', 'Paz'].map((day) => (
                    <div key={day} className="rounded-lg bg-gray-50 py-2.5 text-center text-sm font-bold text-black">{day}</div>
                  ))}
                </div>
                <div className="grid grid-cols-7 gap-1">
                  {(() => {
                    const firstDay = new Date(calendarYear, calendarMonth - 1, 1).getDay();
                    const emptyStart = firstDay === 0 ? 6 : firstDay - 1;
                    const cells: React.ReactNode[] = [];
                    for (let i = 0; i < emptyStart; i++) {
                      cells.push(<div key={`empty-${i}`} className="min-h-[90px] rounded-lg bg-gray-50/50" />);
                    }
                    for (let day = 1; day <= new Date(calendarYear, calendarMonth, 0).getDate(); day++) {
                      const dateStr = `${calendarYear}-${String(calendarMonth).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
                      const dayData = calendarData.find((d) => d.date === dateStr);
                      const isToday = dateStr === new Date().toISOString().slice(0, 10);
                      cells.push(
                        <button
                          type="button"
                          key={dateStr}
                          onClick={() => { if ((dayData?.onLeaveCount ?? 0) > 0) setSelectedCalendarDay(dayData || null); }}
                          className={`min-h-[104px] rounded-lg border p-2.5 text-left transition-colors ${isToday ? 'border-blue-400 bg-blue-50' : 'border-gray-200 hover:border-blue-300 hover:bg-blue-50/30'} ${(dayData?.onLeaveCount ?? 0) > 0 ? 'cursor-pointer' : 'cursor-default'}`}
                        >
                          <div className="mb-1.5 text-lg font-bold text-black">{day}</div>
                          <div className="flex flex-col gap-1">
                            <div className="flex items-center gap-1.5 text-sm">
                              <Users className="h-4 w-4 text-emerald-600" />
                              <span className="font-semibold text-black">{dayData?.activeCount ?? '-'}</span>
                              <span className="font-medium text-black">/ {dayData?.totalDrivers ?? '-'}</span>
                            </div>
                            {(dayData?.onLeaveCount ?? 0) > 0 && (
                              <div className="flex items-center gap-1.5 text-sm">
                                <UserCheck className="h-4 w-4 text-amber-600" />
                                <span className="font-semibold text-black">{dayData?.onLeaveCount} izinli</span>
                              </div>
                            )}
                            {dayData?.onLeaveDrivers?.length > 0 && (
                              <div className="mt-1 text-xs font-medium text-black truncate" title={dayData.onLeaveDrivers.map((item) => item.driverName).join(', ')}>
                                Detay için tıklayın
                              </div>
                            )}
                          </div>
                        </button>
                      );
                    }
                    return cells;
                  })()}
                </div>
                <div className="mt-4 flex flex-wrap items-center gap-4 text-xs text-black">
                  <div className="flex items-center gap-1"><Users className="h-3 w-3 text-emerald-600" /> Aktif sürücü</div>
                  <div className="flex items-center gap-1"><UserCheck className="h-3 w-3 text-amber-600" /> İzinli sürücü</div>
                  <div className="flex items-center gap-1"><CalendarIcon className="h-3 w-3 text-blue-400" /> Bugün</div>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {selectedCalendarDay && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200 flex items-center justify-between sticky top-0 bg-white">
              <div>
                <h2 className="text-xl font-bold text-gray-900">İzinli Personel Detayı</h2>
                <p className="mt-1 text-sm text-gray-500">{new Date(selectedCalendarDay.date).toLocaleDateString('tr-TR')} tarihinde izinli personeller</p>
              </div>
              <button onClick={() => setSelectedCalendarDay(null)} className="text-gray-400 hover:text-gray-600">
                <X className="w-6 h-6" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
                  <div className="text-xs text-gray-500">Tarih</div>
                  <div className="mt-1 font-semibold text-gray-900">{new Date(selectedCalendarDay.date).toLocaleDateString('tr-TR')}</div>
                </div>
                <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
                  <div className="text-xs text-gray-500">İzinli Personel</div>
                  <div className="mt-1 font-semibold text-amber-700">{selectedCalendarDay.onLeaveCount}</div>
                </div>
                <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
                  <div className="text-xs text-gray-500">Aktif / Toplam</div>
                  <div className="mt-1 font-semibold text-emerald-700">{selectedCalendarDay.activeCount} / {selectedCalendarDay.totalDrivers}</div>
                </div>
              </div>

              <div className="overflow-x-auto rounded-lg border border-gray-200">
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left font-semibold text-gray-600">Sürücü</th>
                      <th className="px-4 py-3 text-left font-semibold text-gray-600">Telefon</th>
                      <th className="px-4 py-3 text-left font-semibold text-gray-600">Çekici</th>
                      <th className="px-4 py-3 text-left font-semibold text-gray-600">Dorse</th>
                      <th className="px-4 py-3 text-left font-semibold text-gray-600">İzin Türü</th>
                      <th className="px-4 py-3 text-left font-semibold text-gray-600">Başlangıç</th>
                      <th className="px-4 py-3 text-left font-semibold text-gray-600">Bitiş</th>
                      <th className="px-4 py-3 text-left font-semibold text-gray-600">Sebep</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {selectedCalendarDay.onLeaveDrivers.map((item) => (
                      <tr key={`${selectedCalendarDay.date}-${item.driverId}`} className="hover:bg-gray-50">
                        <td className="px-4 py-3 font-medium text-gray-900">{item.driverName}</td>
                        <td className="px-4 py-3 text-gray-700">{item.phone || '-'}</td>
                        <td className="px-4 py-3 text-gray-700">{item.vehiclePlate || '-'}</td>
                        <td className="px-4 py-3 text-gray-700">{item.trailerPlate || '-'}</td>
                        <td className="px-4 py-3 text-gray-700">{item.leaveType || '-'}</td>
                        <td className="px-4 py-3 text-gray-700">{new Date(item.startDate).toLocaleDateString('tr-TR')}</td>
                        <td className="px-4 py-3 text-gray-700">{new Date(item.endDate).toLocaleDateString('tr-TR')}</td>
                        <td className="px-4 py-3 text-gray-700">{item.reason || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}

      {isFormOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200 flex items-center justify-between sticky top-0 bg-white">
              <h2 className="text-xl font-bold text-gray-900">{editingItem ? 'İzin Talebi Düzenle' : 'Yeni İzin Talebi Ekle'}</h2>
              <button onClick={closeForm} className="text-gray-400 hover:text-gray-600">
                <X className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Sürücü Adı *</label>
                  <div className="relative">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <input
                        value={formData.driverName || driverSearch}
                        onChange={(e) => {
                          setDriverSearch(e.target.value);
                          setFormData({ ...formData, driverId: '', driverName: '' });
                          setShowDriverList(true);
                        }}
                        onFocus={() => setShowDriverList(true)}
                        className="w-full border border-gray-300 rounded-lg pl-10 pr-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Sürücü ara"
                      />
                    </div>
                    {showDriverList && (
                      <div className="absolute z-20 mt-2 w-full max-h-64 overflow-auto rounded-lg border border-gray-200 bg-white shadow-lg">
                        {filteredDriverOptions.map((driver) => (
                          <button key={driver.id} type="button" onClick={() => { setFormData({ ...formData, driverId: driver.id, driverName: driver.name }); setDriverSearch(driver.name); setShowDriverList(false); }} className="block w-full px-4 py-2 text-left hover:bg-gray-50">
                            {driver.name}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">İzin Türü *</label>
                  <select
                    required
                    value={formData.leaveType || ''}
                    onChange={(e) => setFormData({ ...formData, leaveType: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Seçiniz</option>
                    <option value="Yıllık İzin">Yıllık İzin</option>
                    <option value="Hastalık İzni">Hastalık İzni</option>
                    <option value="Mazeret İzni">Mazeret İzni</option>
                    <option value="Ücretsiz İzin">Ücretsiz İzin</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Başlangıç Tarihi *</label>
                  <input type="date" required value={formData.startDate || ''} onChange={(e) => setFormData({ ...formData, startDate: e.target.value })} className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Bitiş Tarihi *</label>
                  <input type="date" required value={formData.endDate || ''} onChange={(e) => setFormData({ ...formData, endDate: e.target.value })} className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Talep Tarihi *</label>
                  <input type="date" required value={formData.requestDate || ''} onChange={(e) => setFormData({ ...formData, requestDate: e.target.value })} className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Durum</label>
                  <input value={editingItem?.status || 'Beklemede'} disabled className="w-full border border-gray-200 bg-gray-50 rounded-lg px-3 py-2 text-gray-500" />
                </div>

                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Sebep *</label>
                  <textarea required value={formData.reason || ''} onChange={(e) => setFormData({ ...formData, reason: e.target.value })} rows={2} className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>

                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Notlar</label>
                  <textarea value={formData.notes || ''} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} rows={2} className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>

                <div className="col-span-2 rounded-lg border border-gray-200 bg-gray-50 p-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Belge</label>
                  <p className="mb-3 text-xs text-gray-500">Zorunlu değildir. Fotoğraf veya PDF yükleyebilirsiniz.</p>
                  {editingItem?.documents?.[0] && (
                    <div className="mb-3 flex flex-wrap items-center gap-2 rounded-lg border border-blue-100 bg-white px-3 py-2 text-sm text-gray-700">
                      <FileText className="h-4 w-4 text-blue-600" />
                      <span className="min-w-0 flex-1 truncate">{editingItem.documents[0].fileName}</span>
                      <button type="button" onClick={() => window.open(resolveFileUrl(editingItem.documents?.[0]?.filePath), '_blank', 'noopener,noreferrer')} className="inline-flex items-center gap-1 text-blue-700 hover:text-blue-900">
                        <Eye className="h-4 w-4" />
                        Görüntüle
                      </button>
                    </div>
                  )}
                  <input
                    type="file"
                    accept="image/*,.pdf,application/pdf"
                    onChange={(event) => {
                      const file = event.target.files?.[0] || null;
                      if (file && !file.type.startsWith('image/') && file.type !== 'application/pdf' && !file.name.toLocaleLowerCase('tr-TR').endsWith('.pdf')) {
                        showAlert({ tone: 'error', title: 'Geçersiz Dosya', message: 'Sadece fotoğraf veya PDF yükleyebilirsiniz.' });
                        event.target.value = '';
                        setLeaveDocumentFile(null);
                        return;
                      }
                      setLeaveDocumentFile(file);
                    }}
                    className="block w-full cursor-pointer rounded-lg border border-gray-300 bg-white text-sm text-gray-700 file:mr-4 file:border-0 file:bg-blue-50 file:px-4 file:py-2 file:text-sm file:font-medium file:text-blue-700 hover:file:bg-blue-100"
                  />
                  {leaveDocumentFile && <p className="mt-2 text-xs text-gray-600">Seçilen dosya: {leaveDocumentFile.name}</p>}
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
                <button type="button" onClick={closeForm} className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50">İptal</button>
                <button type="submit" disabled={isSaving} className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-60">
                  {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                  {editingItem ? 'Güncelle' : 'Kaydet'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {balanceFormOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-xl w-full">
            <div className="p-6 border-b border-gray-200 flex items-center justify-between">
              <h2 className="text-xl font-bold text-gray-900">İzin Hakkı İşle</h2>
              <button onClick={closeBalanceForm} className="text-gray-400 hover:text-gray-600"><X className="w-6 h-6" /></button>
            </div>
            <form onSubmit={submitBalanceTransaction} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Sürücü *</label>
                <select value={balanceForm.driverId} onChange={(e) => setBalanceForm((current) => ({ ...current, driverId: e.target.value }))} className="w-full border border-gray-300 rounded-lg px-3 py-2">
                  <option value="">Seçiniz</option>
                  {leaveBalances.map((item) => <option key={item.driverId} value={item.driverId}>{item.driverName}</option>)}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">İşlem Tipi *</label>
                  <select value={balanceForm.transactionType} onChange={(e) => setBalanceForm((current) => ({ ...current, transactionType: e.target.value }))} className="w-full border border-gray-300 rounded-lg px-3 py-2">
                    <option value="manual-add">Eklendi</option>
                    <option value="manual-subtract">Çıkarıldı</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Gün *</label>
                  <input type="number" step="0.5" min="0" value={balanceForm.days} onChange={(e) => setBalanceForm((current) => ({ ...current, days: e.target.value }))} className="w-full border border-gray-300 rounded-lg px-3 py-2" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Açıklama</label>
                <textarea value={balanceForm.description} onChange={(e) => setBalanceForm((current) => ({ ...current, description: e.target.value }))} rows={3} className="w-full border border-gray-300 rounded-lg px-3 py-2" placeholder="Örn: Yıllık hakediş eklendi" />
              </div>
              <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
                <button type="button" onClick={closeBalanceForm} className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50">İptal</button>
                <button type="submit" disabled={balanceSaving} className="flex items-center gap-2 bg-emerald-600 text-white px-4 py-2 rounded-lg hover:bg-emerald-700 disabled:opacity-60">
                  {balanceSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                  Kaydet
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
